import React, { useMemo, useEffect } from 'react';

import classNames from 'classnames';
import {
  Dialog as MUIDialog,
  DialogProps as MUIDialogProps,
} from '@mui/material';

import { useIsInsideContainer } from '../hooks';

import { useStyles } from './Dialog.styles';

export interface DialogProps extends MUIDialogProps {
  withoutRadius?: boolean;
  fullScreen?: boolean;
}

/**
 * Компонент диалога, который расширяет диалоговый компонент Material-UI дополнительными опциями стилизации.
 *
 * @param {Object} props - Свойства, которые можно передать в компонент Dialog.
 * @param {boolean} [props.fullScreen=false] - Если true, диалог будет полноэкранным на мобильных устройствах.
 * @param {boolean} [props.withoutRadius] - Если true, диалог не будет иметь закруглённых углов.
 * @param {ReactNode} props.children - Содержимое, которое будет отображаться внутри диалога.
 * @param {Partial<MUIDialogProps['classes']>} [props.classes] - Пользовательские CSS-классы для переопределения стилей по умолчанию.
 * @param {boolean} props.open - Если true, диалог открыт.
 * @param {...MUIDialogProps} props - Дополнительные свойства, которые можно передать в компонент Dialog Material-UI.
 * @returns {JSX.Element | null} JSX-элемент, представляющий диалог, если он открыт, в противном случае null.
 */

const SCROLLABLE_DIALOG_ID = 'scrollable-dialog';

export const Dialog = ({
  fullScreen = false,
  withoutRadius,
  children,
  classes,
  open,
  ...props
}: DialogProps) => {
  const { classes: styles } = useStyles();
  const isInsideContainer = useIsInsideContainer();

  const modalClasses = useMemo(
    () => ({
      ...classes,
      paper: classNames(
        styles.dialogPaper,
        classes?.paper,
        withoutRadius && styles.dialogWithoutRadius,
      ),
    }),
    [classes, styles, withoutRadius],
  );

  useEffect(() => {
    if (isInsideContainer) {
      document.body.style.overflow = open ? 'hidden' : '';
    }

    return () => {
      if (isInsideContainer) {
        document.body.style.overflow = '';
      }
    };
  }, [open, isInsideContainer]);

  if (open) {
    return (
      <MUIDialog
        classes={modalClasses}
        fullScreen={fullScreen}
        className={classNames('aqa_dialog', props.className)}
        open={open}
        data-testid="dialog-test-id"
        PaperProps={{
          id: SCROLLABLE_DIALOG_ID,
        }}
        {...props}
      >
        {children}
      </MUIDialog>
    );
  }

  return null;
};
